import { useEffect, useState } from "react";

import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";

import { getPhoneNumberError } from "components/PhoneNumberField";

import { SignUp } from "./SignUp";
import { EMPTY_SIGNUP_PHONE_ERRORS, PERSONA_TO_ROLE } from "./types";
import { ClientAppEvent, E164_MIN_DIGIT_COUNT } from "../../../../constants";
import {
  useAuthContext,
  usePasswordFieldToggle,
  useRecaptcha,
  useSignupForm,
  useSignupSubmission,
} from "../../../../hooks";

import type { SignUpData, SignupPhoneErrors, SignupPhoneField } from "./types";

export function SignUpContainer() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { isAuthenticated } = useAuthContext();
  const { passwordIsVisible, handleShowPasswordChange } =
    usePasswordFieldToggle();

  const { recaptchaToken, executeReCaptcha } = useRecaptcha();

  const {
    form,
    formError,
    termsAccepted,
    handleFormChange: handleFormFieldChange,
    handleTermsChange,
    validateEmailMatch,
  } = useSignupForm<SignUpData>({
    initialFormState: {
      email: "",
      confirmEmail: "",
      password: "",
      firstName: "",
      lastName: "",
      workPhoneNumber: "",
      workPhoneNumberExtension: "",
      cellPhoneNumber: "",
      persona: undefined,
      recaptchaToken,
    },
  });

  const { loading, signupError, handleSignup, setSignupError } =
    useSignupSubmission({
      executeReCaptcha,
    });

  const [phoneErrors, setPhoneErrors] = useState<SignupPhoneErrors>(
    EMPTY_SIGNUP_PHONE_ERRORS,
  );

  // if user is already auhenticated redirect then to the homepage
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const validatePhoneField = (field: SignupPhoneField): string | null =>
    getPhoneNumberError(form[field], {
      required: field === "workPhoneNumber",
    });

  const handlePhoneFieldBlur = (field: SignupPhoneField) => {
    setPhoneErrors((prev) => ({ ...prev, [field]: validatePhoneField(field) }));
  };

  // Clear a phone field's error as the user edits it; blur/submit re-validates.
  const handleFormChange = (key: keyof SignUpData, value: string) => {
    handleFormFieldChange(key, value);
    if (
      (key === "workPhoneNumber" || key === "cellPhoneNumber") &&
      phoneErrors[key] != null
    ) {
      setPhoneErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const onSignup = () => {
    const {
      email,
      password,
      firstName,
      lastName,
      workPhoneNumber,
      workPhoneNumberExtension,
      cellPhoneNumber,
      persona,
      confirmEmail,
    } = form;

    const emailError = validateEmailMatch(email, confirmEmail);
    if (emailError) {
      setSignupError(emailError);
      posthog?.capture(ClientAppEvent.SIGNUP_ERROR, {
        error_code: "EMAIL_MISMATCH_CLIENT",
        email,
      });
      return;
    }

    if (!persona) {
      setSignupError(
        "Please select whether you are a shipper, carrier or broker.",
      );
      posthog?.capture(ClientAppEvent.SIGNUP_ERROR, {
        error_code: "ROLE_MISSING_CLIENT",
        email,
      });
      return;
    }

    const nextPhoneErrors: SignupPhoneErrors = {
      workPhoneNumber: validatePhoneField("workPhoneNumber"),
      cellPhoneNumber: validatePhoneField("cellPhoneNumber"),
    };
    setPhoneErrors(nextPhoneErrors);
    if (
      nextPhoneErrors.workPhoneNumber != null ||
      nextPhoneErrors.cellPhoneNumber != null
    ) {
      // Client-side rejections never reach the server, so without this event
      // the failure is invisible. The _CLIENT suffix distinguishes it from the
      // backend INVALID_PHONE_NUMBER, and the attempted values (masked in
      // replay) let support see the number entered.
      posthog?.capture(ClientAppEvent.SIGNUP_ERROR, {
        error_code: "INVALID_PHONE_NUMBER_CLIENT",
        email,
        persona,
        attempted_work_phone: workPhoneNumber,
        attempted_cell_phone: cellPhoneNumber,
        attempted_extension: workPhoneNumberExtension,
      });
      return;
    }

    posthog?.capture(ClientAppEvent.SIGNUP_SUBMITTED, { email, persona });

    handleSignup({
      email,
      password,
      firstName,
      lastName,
      workPhoneNumber,
      workPhoneNumberExtension,
      cellPhoneNumber,
      recaptchaToken,
      role: PERSONA_TO_ROLE[persona],
      persona,
    });
  };

  return (
    <SignUp
      onFormChange={handleFormChange}
      onSignup={onSignup}
      onPhoneFieldBlur={handlePhoneFieldBlur}
      persona={form.persona}
      workPhoneNumber={form.workPhoneNumber}
      workPhoneNumberExtension={form.workPhoneNumberExtension}
      cellPhoneNumber={form.cellPhoneNumber}
      formError={formError}
      phoneErrors={phoneErrors}
      showPassword={passwordIsVisible}
      onShowPasswordChange={handleShowPasswordChange}
      signupDisabled={
        form.email.length === 0 ||
        form.password.length === 0 ||
        form.firstName.length === 0 ||
        form.lastName.length === 0 ||
        form.workPhoneNumber.replace(/\D/g, "").length < E164_MIN_DIGIT_COUNT ||
        formError.email != null ||
        formError.password != null ||
        !termsAccepted
      }
      signupError={signupError}
      onTermsChange={handleTermsChange}
      loading={loading}
    />
  );
}
